import React from 'react'
import { graphql, navigate, useStaticQuery } from 'gatsby'

import SEO from '../components/seo'
import NewsSectionBlock from '../components/NewsSectionBlock'
import Typography from '../components/Common/Typography'

import BannerSection from '../components/BannerSection'
import List from '../components/Common/List'
import Carousel from '../components/Carousel'
import { TImageStaticQuery } from '../types/pages'
import NewsQuoteBlock from '../components/NewsQuoteBlock'
import { GlobalContext } from '../context/GlobalContext'
import Link from '../components/Common/Link'
import { useTranslations } from '../hooks/use-translations'

const EmpoweringIraqFuture = () => {
  const carouselData0 = [
    {
      imageId: 'carouselData0image1',
      alt: 'Project-overview-1',
    },
    {
      imageId: 'carouselData0image2',
      alt: 'Project-overview-2',
    },
    {
      imageId: 'carouselData0image3',
      alt: 'Project-overview-3',
    },
  ]

  const carouselData1 = [
    {
      imageId: 'carouselData1image1',
      alt: 'Preparation-phase-1',
    },
    {
      imageId: 'carouselData1image2',
      alt: 'Preparation-phase-2',
    },
  ]

  const carouselData2 = [
    {
      imageId: 'carouselData2image1',
      alt: 'Centralized-Document-Storage-&-Version-Control-1',
    },
    {
      imageId: 'carouselData2image2',
      alt: 'Centralized-Document-Storage-&-Version-Control-2',
    },
    {
      imageId: 'carouselData2image3',
      alt: 'Centralized-Document-Storage-&-Version-Control-3',
    },
  ]

  const carouselData3 = [
    {
      imageId: 'carouselData3image1',
      alt: 'Streamlined-Collaboration-with-Digital-Notes-&-Workflows-1',
    },
    {
      imageId: 'carouselData3image2',
      alt: 'Streamlined-Collaboration-with-Digital-Notes-&-Workflows-1-2',
    },
    {
      imageId: 'carouselData3image3',
      alt: 'Streamlined-Collaboration-with-Digital-Notes-&-Workflows-1-3',
    },
  ]

  const carouselData4 = [
    {
      imageId: 'carouselData4image1',
      alt: 'Automated-Approvals-&-Transparent-Tracking-1',
    },
    {
      imageId: 'carouselData4image2',
      alt: 'Automated-Approvals-&-Transparent-Tracking-2',
    },
  ]

  const { isDesktop } = React.useContext(GlobalContext)
  const { getPath } = useTranslations()
  const images = useStaticQuery(imagesQuery) as TImageStaticQuery

  return (
    <>
      <SEO
        title="Empowering Iraq’s Future: Shams Group’s Digital Transformation at Umm Qasr Port"
        description="Discover how Shamsh Group modernized Umm Qasr Port’s infrastructure with SIGNAX DOCS, achieving faster approvals, streamlined document management, and improved collaboration across Iraq’s key development project."
      />
      <BannerSection
        imageDesktop={images.banner.childImageSharp.gatsbyImageData}
        title={`Empowering Iraq’s Future: Shamsh\u00a0Group’s Digital Transformation at\u00a0Umm Qasr Port`}
        subTitle={`Discover how Shamsh\u00a0Group modernized Umm Qasr Port’s infrastructure with\u00a0SIGNAX\u00a0DOCS, achieving faster approvals, streamlined document management, and\u00a0improved collaboration across Iraq’s key development project`}
        titleSize={isDesktop ? 60 : 40}
        colorShadow="dark"
        imgStyle={{ height: '467px' }}
      />
      <NewsSectionBlock date="March 25, 2025">
        <Typography variant="h3" className="mt-0">
          Project overview
        </Typography>
        <Typography variant="body1">
          Umm Qasr Port, Iraq’s largest port by&nbsp;cargo volume and its only
          deep-water port, is&nbsp;being modernized to&nbsp;expand cargo
          capacity and operational efficiency. Located near Basra, the port
          serves as&nbsp;Iraq’s primary gateway for imports, handling&nbsp;60%
          of&nbsp;the country’s cargo.
        </Typography>
        <Typography variant="body1">
          The project aligns with Iraq’s $17-billion «Development Road»
          initiative. It&nbsp;will link Grand Faw Port to&nbsp;Turkey and Europe
          and position Iraq as&nbsp;a&nbsp;key Middle East-West transit
          corridor.
        </Typography>
        <Typography variant="body1">
          The project is&nbsp;being spearheaded by&nbsp;AD&nbsp;Ports Group,
          a&nbsp;leading developer and regulator of&nbsp;ports and related
          infrastructure in&nbsp;the MENA region, in&nbsp;collaboration with
          Raiya Group, a&nbsp;prominent Iraqi construction company, as&nbsp;the
          investor. The first phase handover is&nbsp;scheduled for&nbsp;2028.
          Shamsh Group has been entrusted with the construction of&nbsp;more
          than 20&nbsp;new administrative and operational facilities, further
          strengthening the port’s infrastructure and functionality.
        </Typography>
        <Carousel
          className="mt-4"
          images={images}
          carouselData={carouselData0}
          centeredSlides={true}
        />
        <Typography variant="h3">Preparation Phase</Typography>
        <List>
          <li>
            <span style={{ fontWeight: 500 }}>Challenge</span>
            <Typography
              variant="body1"
              style={{ textIndent: 0, marginTop: '4px' }}
            >
              Implement a training program to train a dispersed team with varied
              schedules effectively.
            </Typography>
          </li>
        </List>
        <Typography variant="body1">
          Before the project kick-off, SIGNAX collaborated closely with Shamsh
          Group’s Project Lead, Thulfiqar Salhom, through structured workshops
          to&nbsp;align objectives, timelines, and resources. We&nbsp;identified
          and analyzed the needs, leading to&nbsp;the decision to&nbsp;implement{' '}
          <Typography
            variant="body2"
            color="blue"
            onClick={() => navigate(getPath('/docs/'))}
          >
            DOCS CDE
          </Typography>
          .
        </Typography>
        <Typography variant="body1">
          CDE (Common Data Environment) is&nbsp;a&nbsp;centralized platform for
          managing and sharing project information, ensuring real-time
          collaboration and compliance with standards like ISO 19650.
        </Typography>
        <Typography variant="body1">
          Implementing new software into a&nbsp;company’s business processes
          is&nbsp;a&nbsp;complex and labor-intensive task, which was the main
          challenge we&nbsp;successfully addressed.
        </Typography>
        <Typography variant="body1">
          A&nbsp;hybrid training program was deployed to&nbsp;ensure the
          seamless adoption of&nbsp;SIGNAX solutions, which included:
        </Typography>
        <List>
          <li>Offline training sessions and workshops</li>
          <li>
            On-demand online course with{' '}
            <Link
              href="https://www.udemy.com/course/signax-docs-common-data-environment"
              rel="nofollow noreferrer"
              target="_blank"
            >
              certification
            </Link>
          </li>
          <li>
            Convenient{' '}
            <Link
              href="https://wiki.signax.io"
              rel="nofollow noreferrer"
              target="_blank"
            >
              wiki
            </Link>
          </li>
          <li>Role-based simulations for better further collaboration</li>
          <li>QA sessions and fast support</li>
        </List>
        <Typography variant="body1">
          Within 30&nbsp;days, all team members completed certified training,
          enabling a&nbsp;rapid transition to&nbsp;digital workflows. This phase
          established a&nbsp;foundation for standardized processes
          in&nbsp;document management, eliminating the challenges
          of&nbsp;mastering new software and improving cross-departmental
          collaboration.
        </Typography>
        <List style={{ listStylePosition: 'inside' }}>
          <li>
            <span style={{ fontWeight: 500 }}>Impact</span>
            <ol>
              <li>{`Accelerated software rollout (<30 days)  and workflow standardization.`}</li>
              <li>100% team members got certified in SIGNAX DOCS.</li>
            </ol>
          </li>
        </List>
        <Carousel
          className="mt-4"
          images={images}
          carouselData={carouselData1}
          centeredSlides={true}
          autoHeight
        />
        <Typography variant="h3">Use cases and benefits</Typography>
        <Typography variant="body1" style={{ fontWeight: 500 }}>
          1. Centralized Document Storage & Version Control
        </Typography>
        <List>
          <li>
            <span style={{ fontWeight: 500 }}>Challenge</span>
            <Typography
              variant="body1"
              style={{ textIndent: 0, marginTop: '4px' }}
            >
              Decentralized storage systems (SharePoint, Google Docs, paper
              files) cause version conflicts, collaboration bottlenecks, and
              inefficient access management.
            </Typography>
          </li>
        </List>
        <Typography variant="body1">
          One of&nbsp;the first significant improvements was organizing document
          storage. Before SIGNAX, project documents were scattered across
          various platforms: paper-based files, scans stored in&nbsp;SharePoint,
          and CAD/BIM models saved in&nbsp;SharePoint or&nbsp;Google Drive. This
          led to&nbsp;collaboration challenges and inefficiencies
          in&nbsp;version control. Finding the latest file versions was
          challenging and potentially led to&nbsp;costly mistakes.
        </Typography>
        <Typography variant="body1">
          After the training, Thulfiqar established a&nbsp;structured project
          folder system with custom role-based access. SIGNAX technical support,
          leveraging open API protocols, helped Shamsh Group migrate all project
          data.
        </Typography>
        <Typography variant="body1">
          The new system empowered all stakeholders, including Thulfiqar’s team
          at&nbsp;Shamsh Group, client representatives from AD&nbsp;Ports Group
          and Raiya Group, and subcontractors, with tailored access permissions.
          Each party had their unique permissions to&nbsp;view, edit,
          or&nbsp;comment on&nbsp;documents based on&nbsp;their role.
        </Typography>
        <List style={{ listStylePosition: 'inside' }}>
          <li>
            <span style={{ fontWeight: 500 }}>Impact</span>
            <ol>
              <li>43% reduction in time spent searching for documents.</li>
              <li>
                Eliminated versioning errors, ensuring all teams reference the
                latest CAD/BIM models and documentation.
              </li>
            </ol>
          </li>
        </List>
        <Carousel
          className="mt-4"
          images={images}
          carouselData={carouselData2}
          centeredSlides={true}
        />
        <Typography variant="body1" style={{ fontWeight: 500 }}>
          2. Streamlined Collaboration with Digital Notes & Workflows
        </Typography>
        <List>
          <li>
            <span style={{ fontWeight: 500 }}>Challenge</span>
            <Typography
              variant="body1"
              style={{ textIndent: 0, marginTop: '4px' }}
            >
              Paper-based markups and email/messenger communication delay
              decisions and create accountability gaps.
            </Typography>
          </li>
        </List>
        <Typography variant="body1">
          Clear and transparent communication is&nbsp;key to&nbsp;meeting
          project deadlines. Before SIGNAX, teams relied on&nbsp;emails and
          messaging apps to&nbsp;exchange feedback, making tracking issues,
          maintaining workflow consistency, or&nbsp;efficiently addressing
          file-specific notes nearly impossible. Making changes to&nbsp;drawings
          and documents on&nbsp;construction sites often meant excessive
          paperwork and wasted time.
        </Typography>
        <Typography variant="body1">
          SIGNAX DOCS transformed this process by&nbsp;digitizing collaboration.
          Shamsh Group’s document manager, Saeed Fares, set up&nbsp;custom
          templates for tracking issues directly within project files, managing
          access, and defining viewing/editing permissions. Additionally,
          previously, paper-based workflows were digitalized. Team members added
          notes and digital signatures and created Request for Inspection (RFI),
          Material Approval Requests (MAR), Material Inspection Requests (MIR),
          Non-Compliance Reports (NCR), and other documentation directly from
          mobile devices.
        </Typography>
        <Typography variant="body1">
          With automated notifications, responsible team members received
          instant email alerts when an&nbsp;issue is&nbsp;assigned. Then, they
          tracked, updated, and resolved tasks efficiently, significantly
          improving response times.
        </Typography>
        <List style={{ listStylePosition: 'inside' }}>
          <li>
            <span style={{ fontWeight: 500 }}>Impact</span>
            <ol>
              <li>27% faster resolution of site issues.</li>
              <li>55% reduction in paper-based workflows.</li>
            </ol>
          </li>
        </List>
        <Carousel
          className="mt-4"
          images={images}
          carouselData={carouselData3}
          centeredSlides={true}
        />
        <Typography variant="body1" style={{ fontWeight: 500 }}>
          3. Automated Approvals & Transparent Tracking
        </Typography>
        <List>
          <li>
            <span style={{ fontWeight: 500 }}>Challenge</span>
            <Typography
              variant="body1"
              style={{ textIndent: 0, marginTop: '4px' }}
            >
              Manual approvals via email/WhatsApp cause delays, rework, and
              miscommunication.
            </Typography>
          </li>
        </List>
        <Typography variant="body1">
          Before SIGNAX, document approvals followed an&nbsp;outdated workflow:
          files were emailed or&nbsp;shared via WhatsApp, printed, signed,
          scanned, and&nbsp;sent back – a time-consuming and error-prone
          process.
        </Typography>
        <Typography variant="body1">
          By&nbsp;implementing SIGNAX DOCS, approvals were entirely digitized,
          drastically reducing turnaround times. Custom roles and workflows were
          set&nbsp;up, allowing documents to&nbsp;be&nbsp;reviewed and approved
          seamlessly within the system. Approved drawings were stamped with
          a&nbsp;QR code, preventing outdated versions from being used on-site.
        </Typography>
        <Typography variant="body1">
          The introduction of&nbsp;transmittal functionality further streamlined
          communication, enabling secure document handover to&nbsp;clients and
          generating signed Certificates of&nbsp;Delivery and Acceptance. The
          new approval workflow has even been extended beyond construction
          documents – financial documents for the Umm Qasr Port project were
          processed through SIGNAX DOCS as&nbsp;well.
        </Typography>
        <Typography variant="body1">
          A&nbsp;notable use case was the approval of&nbsp;advanced container
          handling equipment for port operations. The process involved multiple
          stakeholders, including the procurement and engineering teams,
          as&nbsp;well as&nbsp;multiple senior managers for final sign-off.
          Traditionally, this would have required sequential reviews, with each
          department waiting for the previous one to&nbsp;complete its
          assessment. With SIGNAX’s flexible approval workflows, the teams were
          able to&nbsp;configure a&nbsp;parallel review system. This allowed the
          procurement and engineering departments to&nbsp;conduct their
          evaluations simultaneously while notifications kept everyone updated
          in&nbsp;real time. Once both teams completed their reviews, the
          document was routed to&nbsp;the two senior managers for final
          approval.
        </Typography>
        <List style={{ listStylePosition: 'inside' }}>
          <li>
            <span style={{ fontWeight: 500 }}>Impact</span>
            <ol>
              <li>Approval cycles cut by over 60%.</li>
              <li>
                No incidents of rework due to on-site QR code verification.
              </li>
            </ol>
          </li>
        </List>
        <Carousel
          className="mt-4"
          images={images}
          carouselData={carouselData4}
          centeredSlides={true}
        />
        <Typography variant="h3">Conclusion</Typography>
        <Typography variant="body1">
          The partnership between SIGNAX and Shamsh Group at&nbsp;Umm Qasr Port
          marks a&nbsp;milestone in&nbsp;Iraq’s infrastructure modernization.
          By&nbsp;digitizing document storage and approval workflows, SIGNAX
          eliminated inefficiencies that once consumed&nbsp;30% of&nbsp;the
          team’s time, allowing Shamsh Group to&nbsp;prioritize critical
          construction milestones.
        </Typography>
        <Typography variant="body1">
          Today, all users operate from a&nbsp;unified digital platform that
          serves as&nbsp;a&nbsp;single source of&nbsp;truth, ensuring accurate,
          up-to-date project information and fostering enhanced collaboration
          and decision-making across the organization.
        </Typography>
        <NewsQuoteBlock
          avatar={images.author.childImageSharp.gatsbyImageData}
          name="Thulfiqar Salhom on the Project’s Success"
          position=""
          text="“SIGNAX didn’t just digitize our workflows — it transformed how we&nbsp;collaborate. We’re meeting deadlines we&nbsp;once thought unrealistic, and our client trusts&nbsp;us to&nbsp;deliver transparency at&nbsp;every step.”"
        />
        <Typography variant="body1">
          With scalable workflows now fully integrated, Shamsh Group is
          well-positioned to spearhead Iraq’s next-generation construction
          projects and is on track to establish itself as a leading contractor
          in the region.
        </Typography>
      </NewsSectionBlock>
    </>
  )
}

const imagesQuery = graphql`
  query {
    banner: file(
      relativePath: { eq: "news/empowering-iraq-future/banner.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    carouselData0image1: file(
      relativePath: { eq: "news/empowering-iraq-future/slider0-1.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData0image2: file(
      relativePath: { eq: "news/empowering-iraq-future/slider0-2.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData0image3: file(
      relativePath: { eq: "news/empowering-iraq-future/slider0-3.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData1image1: file(
      relativePath: { eq: "news/empowering-iraq-future/slider1-1.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData1image2: file(
      relativePath: { eq: "news/empowering-iraq-future/slider1-2.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 440, placeholder: BLURRED)
      }
    }
    carouselData2image1: file(
      relativePath: { eq: "news/empowering-iraq-future/slider2-1.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData2image2: file(
      relativePath: { eq: "news/empowering-iraq-future/slider2-2.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData2image3: file(
      relativePath: { eq: "news/empowering-iraq-future/slider2-3.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData3image1: file(
      relativePath: { eq: "news/empowering-iraq-future/slider3-1.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData3image2: file(
      relativePath: { eq: "news/empowering-iraq-future/slider3-2.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData3image3: file(
      relativePath: { eq: "news/empowering-iraq-future/slider3-3.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData4image1: file(
      relativePath: { eq: "news/empowering-iraq-future/slider4-1.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData4image2: file(
      relativePath: { eq: "news/empowering-iraq-future/slider4-2.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    author: file(
      relativePath: { eq: "news/empowering-iraq-future/Thulfiqar-Salhom.png" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 100, placeholder: BLURRED)
      }
    }
  }
`

export default EmpoweringIraqFuture
